import React from 'react';
import MaterialIcon from '@/components/MaterialIcon';

/**
 * その他の強力機能 Section
 * Bento Grid Layout with:
 * - 商品検索AI (大卡片 2x2)
 * - 在庫確認、迷子・SOS、クーポン配信、防災・防犯 (小卡片 1x1)
 * - LINE公式アカウント連携 (横長カード 2x1)
 * - オンプレミス、駐車場連携 (灰色卡片 1x1)
 */
export const MoreFeaturesSection: React.FC = () => {
  return (
    <section className="w-full px-4 md:px-10 lg:px-40 py-20 bg-solution-bg">
      <div className="max-w-[1200px] w-full mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-solution-orange font-bold tracking-wider uppercase text-sm mb-2 block">
            AIコンシェルジュのその他の強力機能
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-solution-text mb-4">
            その他の<span className="text-solution-orange">強力</span>機能
          </h2>
          <p className="text-solution-text-secondary text-lg">LINE・オンプレ・駐車場等、多角的DX支援</p>
        </div>

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[140px]">
          {/* 商品検索AI (大卡片 - 2x2) Static UI */}
          <div className="col-span-2 row-span-2 bg-white rounded-2xl border border-solution-border relative overflow-hidden group hover:shadow-xl hover:border-solution-orange/30 transition-all">
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="p-4 border-b border-solution-border">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="text-xs font-bold text-solution-orange">FEATURED</span>
                    <h4 className="font-bold text-lg text-solution-text">商品検索AI</h4>
                  </div>
                  <MaterialIcon name="search" className="text-2xl text-solution-orange/60" />
                </div>
                {/* Static Search Bar */}
                <div className="flex items-center gap-2 bg-solution-bg rounded-lg px-3 py-2">
                  <MaterialIcon name="search" className="text-solution-text-secondary text-lg" />
                  <span className="text-sm text-solution-text">赤いワンピース</span>
                </div>
              </div>
              {/* Static Results */}
              <div className="flex-1 p-3 bg-solution-bg/50 overflow-hidden">
                <p className="text-xs text-solution-text-secondary mb-1.5">3店舗で見つかりました</p>
                <div className="space-y-1.5">
                  {/* Result 1 */}
                  <div className="bg-white rounded-lg p-1.5 flex items-center gap-2 shadow-sm border border-solution-border">
                    <div className="size-8 bg-red-100 rounded flex items-center justify-center text-red-400 shrink-0">
                      <MaterialIcon name="checkroom" className="text-base" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-medium text-solution-text truncate">フローラルワンピース</p>
                      <p className="text-[9px] text-solution-text-secondary">ZARA • 2F</p>
                    </div>
                    <span className="text-[11px] font-bold text-solution-orange">¥5,990</span>
                  </div>
                  {/* Result 2 */}
                  <div className="bg-white rounded-lg p-1.5 flex items-center gap-2 shadow-sm border border-solution-border">
                    <div className="size-8 bg-red-100 rounded flex items-center justify-center text-red-400 shrink-0">
                      <MaterialIcon name="checkroom" className="text-base" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-medium text-solution-text truncate">サマードレス 赤</p>
                      <p className="text-[9px] text-solution-text-secondary">H&M • 3F</p>
                    </div>
                    <span className="text-[11px] font-bold text-solution-orange">¥3,999</span>
                  </div>
                  {/* Result 3 */}
                  <div className="bg-white rounded-lg p-1.5 flex items-center gap-2 shadow-sm border border-solution-border">
                    <div className="size-8 bg-pink-100 rounded flex items-center justify-center text-pink-400 shrink-0">
                      <MaterialIcon name="checkroom" className="text-base" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-medium text-solution-text truncate">Aラインドレス</p>
                      <p className="text-[9px] text-solution-text-secondary">UNIQLO • 1F</p>
                    </div>
                    <span className="text-[11px] font-bold text-solution-orange">¥2,990</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 在庫確認 */}
          <div className="bg-white rounded-2xl p-4 border border-solution-border hover:shadow-lg hover:border-solution-orange/30 transition-all group flex flex-col">
            <MaterialIcon name="inventory_2" className="text-2xl text-solution-orange/70 mb-2" />
            <h4 className="font-bold text-solution-text text-sm mb-1">在庫確認</h4>
            <p className="text-[11px] text-solution-text-secondary flex-1">POS連携でリアルタイム表示</p>
            <p className="text-[10px] text-solution-orange font-medium mt-2">在庫切れ回避 →</p>
          </div>

          {/* 迷子・SOS */}
          <div className="bg-white rounded-2xl p-4 border border-solution-border hover:shadow-lg hover:border-solution-orange/30 transition-all group flex flex-col">
            <MaterialIcon name="notifications_active" className="text-2xl text-solution-orange/70 mb-2" />
            <h4 className="font-bold text-solution-text text-sm mb-1">迷子・SOS</h4>
            <p className="text-[11px] text-solution-text-secondary flex-1">警備員へワンタップ通知</p>
            <p className="text-[10px] text-solution-orange font-medium mt-2">即座対応 →</p>
          </div>

          {/* クーポン配信 */}
          <div className="bg-white rounded-2xl p-4 border border-solution-border hover:shadow-lg hover:border-solution-orange/30 transition-all group flex flex-col">
            <MaterialIcon name="loyalty" className="text-2xl text-solution-orange/70 mb-2" />
            <h4 className="font-bold text-solution-text text-sm mb-1">クーポン配信</h4>
            <p className="text-[11px] text-solution-text-secondary flex-1">会話内容に合わせ自動配信</p>
            <p className="text-[10px] text-solution-orange font-medium mt-2">購買促進 →</p>
          </div>

          {/* 防災・防犯 */}
          <div className="bg-white rounded-2xl p-4 border border-solution-border hover:shadow-lg hover:border-solution-orange/30 transition-all group flex flex-col">
            <MaterialIcon name="security" className="text-2xl text-solution-orange/70 mb-2" />
            <h4 className="font-bold text-solution-text text-sm mb-1">防災・防犯</h4>
            <p className="text-[11px] text-solution-text-secondary flex-1">10言語で避難誘導対応</p>
            <p className="text-[10px] text-solution-orange font-medium mt-2">安全確保 →</p>
          </div>

          {/* LINE公式アカウント連携 (横長カード) */}
          <div className="col-span-2 bg-white rounded-2xl p-4 border border-solution-border relative overflow-hidden group hover:shadow-xl hover:border-solution-orange/30 transition-all">
            <div className="flex items-center gap-4">
              <div className="size-12 bg-[#06C755]/10 rounded-xl flex items-center justify-center shrink-0">
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="#06C755">
                  <path d="M12 2C6.48 2 2 5.82 2 10.5c0 2.55 1.25 4.84 3.23 6.47V22l4.11-2.26c.86.14 1.75.26 2.66.26 5.52 0 10-3.82 10-8.5S17.52 2 12 2z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-base text-solution-text mb-0.5">LINE公式アカウント連携</h4>
                <p className="text-solution-text-secondary text-xs">
                  友だち追加で継続的な関係構築。プッシュ通知でリピーター獲得
                </p>
              </div>
              <div className="shrink-0">
                <span className="inline-flex items-center gap-1 text-[11px] text-[#06C755] font-medium">
                  <MaterialIcon name="trending_up" className="text-sm" />
                  リピート率向上
                </span>
              </div>
            </div>
          </div>

          {/* オンプレミス */}
          <div className="bg-gray-50/50 rounded-2xl p-4 border border-gray-200 relative overflow-hidden group hover:shadow-lg hover:border-gray-300 transition-all flex flex-col">
            <MaterialIcon name="dns" className="text-2xl text-gray-500 mb-2" />
            <h4 className="font-bold text-solution-text text-sm mb-1">オンプレミス</h4>
            <p className="text-[11px] text-solution-text-secondary flex-1">自社サーバーでセキュア運用</p>
            <div className="flex items-center gap-1 mt-2">
              <MaterialIcon name="lock" className="text-gray-400 text-sm" />
              <p className="text-[10px] text-gray-500 font-medium">金融・医療対応</p>
            </div>
          </div>

          {/* 駐車場連携 */}
          <div className="bg-gray-50/50 rounded-2xl p-4 border border-gray-200 relative overflow-hidden group hover:shadow-lg hover:border-gray-300 transition-all flex flex-col">
            <MaterialIcon name="local_parking" className="text-2xl text-gray-500 mb-2" />
            <h4 className="font-bold text-solution-text text-sm mb-1">駐車場連携</h4>
            <p className="text-[11px] text-solution-text-secondary flex-1">空車案内から精算まで</p>
            <div className="flex items-center gap-1 mt-2">
              <MaterialIcon name="directions_car" className="text-gray-400 text-sm" />
              <p className="text-[10px] text-gray-500 font-medium">満車ストレス軽減</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MoreFeaturesSection;
